import { Component, ElementRef, HostListener, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ChartType, ChartConfiguration, Chart, PieController } from 'chart.js';
import { BaseChartDirective } from 'ng2-charts';
import { Subscription, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { ActivityService } from 'src/app/service/activity.service';
import { AgentService } from 'src/app/service/agent.service';
import { AuthenticationService } from 'src/app/service/authentication.service';
import { DataService } from 'src/app/service/data.service';
import { EventService } from 'src/app/service/event.service';
import { SoftwareInventoryService } from 'src/app/service/software-inventory.service';
import { Events, MsgPayLoad, WebsocketService } from 'src/app/service/websocket.service';
import { Utils } from 'src/app/utils';
import { emptyValidator } from 'src/app/validator/empty-form-validator';
import { SolutionInfo } from '../../model/SolutionInfo';
import { ActionMenuEvent, ActionMenuIcon, ActionMenuItem } from '../controls/actionmenu/action-menu.component';
import { ColumnType, DataTableColumn, DataTableRow } from '../controls/datatable/datatable.component';
import { ModalMessageComponent, ModalMessageType } from '../controls/modal-message/modal-message.component';
import { Modal } from '../controls/modal/modal.service';
import { Tab, TabColor } from '../controls/tabs/tabs.component';
import { SystemDetailsTab, SystemsComponent } from '../systems/systems.component';
import { Agent, AgentStatus, Plugin } from './../../model/Agent';
import { DisableProductRequest, DiscoverProductsRequest, SupportedStatusExt } from 'src/app/model/Product';
import { SolutionService } from 'src/app/service/solution.service';
import { ModalConfig } from '../controls/modal/modal-config';

@Component({
    selector: 'app-systems-details',
    templateUrl: './systems-details.component.html',
    styleUrls: ['./systems-details.component.css'],
    standalone: false
})
export class SystemsDetailsComponent extends SystemsComponent implements OnInit {

  SystemDetailTabs = SystemDetailsTab;
  TabColor = TabColor;
  solutionInfo: SolutionInfo = new SolutionInfo();
  selectedSystem: Agent = undefined;
  generalTab = new Tab('general-tab', $localize`General`, undefined, false);
  productsTab = new Tab('products-tab', $localize`Products`, (this.selectedSystem == undefined) ? 0 : this.selectedSystem.products.length, false);
  networkingTab = new Tab('networking-tab', $localize`Networking`, undefined, false);
  operatingSystemTab = new Tab('os-tab', $localize`Operating System`, undefined, false);
  environmentsTab = new Tab('env-tab', $localize`Environments`, (this.selectedSystem == undefined) ? 0 : this.selectedSystem.environments.length, false);
  tabs: Tab[] = [this.generalTab, this.productsTab, this.environmentsTab, this.networkingTab, this.operatingSystemTab];
  activeTab: SystemDetailsTab = SystemDetailsTab.GENERAL;
  products: DataTableRow[] = [];
  productDataColumns: Array<DataTableColumn> = [];
  environments: DataTableRow[] = [];
  environmentsColumns: Array<DataTableColumn> = [];
  selectedSystemId: string;
  selectedProduct: any;
  enableCapabilities: any;
  validateCommunications: boolean;
  selectedUrl: string = "";
  isEditing: boolean = false;
  pluginsDisplayValue: string;
  ipv4DisplayValue: string;
  ipv6DisplayValue: string;
  agentStatus: AgentStatus;
  showMoreActionsMenu: boolean = false;
  @ViewChild('moreActionsAgentMenu') moreActionsMenu: ElementRef;
  
  solutionInfoSubscription: Subscription;
  eventMessageSubscription: Subscription;

  //charts
  totalMemory:string;

  editSystemForm = new FormGroup({
    nickNameFormCtrl: new FormControl('', [
      Validators.required, emptyValidator()
    ]),
    descriptionFormCtrl: new FormControl('', []),
    logLevelFormCtrl: new FormControl('', [
      Validators.required
    ]),
    httpLogLevelFormCtrl: new FormControl('', [
      Validators.required
    ])
  });

  logLevels = [
    { name: $localize`Trace`, value: 'TRACE' },
    { name: $localize`Debug`, value: 'DEBUG' },
    { name: $localize`Info`, value: 'INFO' },
    { name: $localize`Error`, value: 'ERROR' }
  ];

  httpLogLevels = [
    { name: $localize`Trace`, value: 'TRACE' },
    { name: $localize`Debug`, value: 'DEBUG' },
    { name: $localize`Info`, value: 'INFO' },
    { name: $localize`Error`, value: 'ERROR' }
  ];

  systemInfoStatusHandle: any = 0;
  @ViewChildren(BaseChartDirective) charts?: QueryList<BaseChartDirective>;

  //CPU chart data
  cpuChartData: ChartConfiguration['data'] = {
    datasets: [
      { data: [], 
        label: '',
        backgroundColor: 'rgba(4, 102, 52, 0.3)',
        borderColor: 'rgba(4,102,52)',
        pointBackgroundColor: 'rgba(4,102,52,1)',
        pointBorderColor: '#fff',
        pointHoverBackgroundColor: '#fff',
        pointHoverBorderColor: 'rgba(4,102,52,0.8)',
        fill: 'origin',
      }
    ],
    labels: []
  };
  //CPU chart options
  cpuChartOptions: ChartConfiguration['options'] = {
    responsive: true,
    maintainAspectRatio: false,
    elements: {
      line: {
        tension: 0.00001
      }
    },
    scales: {
      y: {
        stacked: true
      }
    },
    plugins: {
      tooltip: {
        enabled: false
      },
      legend: {
        display: false
      }
    }
  };
  //CPU chart type
  cpuChartType: ChartType = 'line';

  //Memory chart data 
  memoryChartData: ChartConfiguration['data'] = {
    datasets: [
      { data: [], 
        label:  $localize`Total`,
        backgroundColor: ['#004358', '#188fe0', '#96d1ef'],
        borderColor: ["#FFFFFF", "#FFFFFF", "#FFFFFF", "#FFFFFF"],
      },
      { data: [],
        label: '',
        hidden: true
      }
    ],
    labels: [],
  };
  //Memory chart options
  memoryChartOptions: ChartConfiguration['options'] = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      tooltip: {
        enabled: false
      },
      legend: {
        display: true,
        position: 'bottom',
      }
    }
  };
  //Memory chart type
  memoryChartType: ChartType = 'pie';

  //Disk chart data 
  diskChartData: ChartConfiguration['data'] = {
    datasets: [
      { data: [], 
        label:  $localize`Total`,
        backgroundColor: ['#004358', '#188fe0', '#96d1ef'],
        borderColor: ["#FFFFFF", "#FFFFFF", "#FFFFFF", "#FFFFFF"],
      },
      { data: [],
        label: '',
        hidden: true
      }
    ],
    labels: [],
  };
  //Disk chart options
  diskChartOptions: ChartConfiguration['options'] = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      tooltip: {
        enabled: false
      },
      legend: {
        display: true,
        position: 'bottom',
      }
    }
  };
  //Disk chart type
  diskChartType: ChartType = 'pie';

  memoryChartPlugin: any = {
    id: 'memoryChartPlugin',
    beforeDraw: function (chart) {
      if (chart.config.type == "pie") {
        var width = chart.width,
          height = chart.height,
          ctx = chart.ctx;

        ctx.restore();
        ctx.textBaseline = "middle";
        ctx.fillStyle = 'black';
        ctx.textAlign = 'center';
        textX = chart.width / 2;
        textY = chart.height / 2 - 30;
        ctx.font = "normal 24px OpenText Sans";
        ctx.fillText(chart.config.data.datasets["1"].label, textX, textY);

        var text = chart.config.data.datasets["0"].label;
        var textX = chart.width / 2;
        var textY = height / 2;

        ctx.font = "normal 12px OpenText Sans";
        ctx.fillText(text, textX, textY);

        ctx.save();
      }
    }
  };


  constructor(
    protected agentService: AgentService,
    protected eventService: EventService,
    protected router: Router,
    protected dataService: DataService,
    protected route: ActivatedRoute,
    protected modal: Modal,
    protected wsService: WebsocketService,
    protected authService: AuthenticationService,
    protected activityService: ActivityService,
    protected softwareService: SoftwareInventoryService,
    protected solutionService: SolutionService) {
    super(agentService, eventService, router, dataService, route, modal, wsService, authService, activityService, solutionService);
    this.productDataColumns = new Array<DataTableColumn>(
      new DataTableColumn("productName", $localize`Product`, ColumnType.STRING, false, "15"),
      new DataTableColumn("componentName", $localize`Component`, ColumnType.STRING, false, "18"),
      new DataTableColumn("productVersion", $localize`Version`, ColumnType.STRING, false, "8"),
      new DataTableColumn("installationDirectory", $localize`Install path`, ColumnType.STRING, false, "15"),
      new DataTableColumn("envStr", $localize`Environment`, ColumnType.STRING, false, "18"),
      new DataTableColumn("discoveryDateTime", $localize`Discovered`, ColumnType.DATEFROMNOW, false, "10"),
      new DataTableColumn("supportedStatusExt", $localize`Supported`, ColumnType.STATUSEXT, false, "7"),
      new DataTableColumn("enabledStatusExt", $localize`Enabled`, ColumnType.STATUSEXT, false, "9")
    );
    this.environmentsColumns = new Array<DataTableColumn>(
      new DataTableColumn("solName", $localize`Solution`, ColumnType.STRING, false, "20"),
      new DataTableColumn("name", $localize`Environment`, ColumnType.STRING, false, "20"),
      new DataTableColumn("description", $localize`Description`, ColumnType.STRING, false, "30"),
      new DataTableColumn("createdBy", $localize`Created by`, ColumnType.STRING, false, "15"),
      new DataTableColumn("createdDate", $localize`Created`, ColumnType.DATEFROMNOW, false, "15")
    );

    if(this.router.currentNavigation() &&
        this.router.currentNavigation().extras && 
        this.router.currentNavigation().extras.state &&
        this.router.currentNavigation().extras.state.activeTab){
            this.activeTab = this.router.currentNavigation().extras.state.activeTab;
    }
  }

  get nickNameFormCtrl() { return this.editSystemForm.get('nickNameFormCtrl'); }
  get descriptionFormCtrl() { return this.editSystemForm.get('descriptionFormCtrl'); }
  get logLevelFormCtrl() { return this.editSystemForm.get('logLevelFormCtrl'); }
  get httpLogLevelFormCtrl() { return this.editSystemForm.get('httpLogLevelFormCtrl'); }

  ngOnInit(): void {
    this.title = $localize`Systems - Details`;

    this.routerSubscription = this.route.params.subscribe(params => {
      this.selectedSystemId = params["agentId"]
    });

    this.eventMessageSubscription = this.eventService.eventMessage$.subscribe(msg => {
      switch (msg.eventType) {
        case Events.AgentRefresh:
        case Events.DisableProduct:
        case Events.AgentStatusOffline:
          this.getAgent();
          break;
        case Events.SystemInformation:
          if(this.isPendingTransaction(msg.headers.transactionId, true)){
            this.processSystemInformation(msg);
          }
          break;
      }
    });

    this.solutionInfoSubscription = this.eventService.currentSolutionInfo$.subscribe(newSolutionInfo => {
      this.solutionInfo = newSolutionInfo;
      this.getAgent();
    });
    Chart.register(PieController);
    Chart.register(this.memoryChartPlugin);
    Chart.overrides.pie.cutout = '90%';
  }

  ngOnDestroy() {
    clearInterval(this.systemInfoStatusHandle);
    this.systemInfoStatusHandle = 0;
    this.routerSubscription?.unsubscribe();
    this.eventMessageSubscription?.unsubscribe();
    this.solutionInfoSubscription?.unsubscribe();
  }

  onSelectedTabChange(selectedIndex: number) {
    var tab = this.tabs[selectedIndex];
    if (tab.id == 'os-tab' && this.selectedSystem.isOnline && this.selectedSystem.isAuthorized && this.selectedSystem.isActive) {
      this.clearOSInterval();
      this.getSystemInfo(true);
    } else {
      this.clearOSInterval();
    }
    this.activeTab = selectedIndex; 
  }

  actionMenuClicked(event: ActionMenuEvent): void {
    if (event.name == "Enable") {
      this.selectedProduct = event.data[0];
      this.router.navigate(["/systems/enable/", this.selectedSystem.agentID, this.selectedProduct.productId, this.selectedProduct.installationDirectory]);
    } else if (event.name == "Disable") {
      this.selectedProduct = event.data[0];
      this.openDisableProductDialog();
    } else if (event.name == "RemoveEnv") {
      const sub: Subscription =
        this.modal.open(ModalMessageComponent, {
          data: {
            type: ModalMessageType.Question,
            title: $localize`Unregister`,
            message: $localize`Do you want to unregister this system from '<b>${event.data[0].solName} : ${event.data[0].name}</b>'? <br/> Any enabled products in this environment will be disabled.`,
            primaryBtn: $localize`Unregister`,
            secondaryBtn: $localize`Cancel`,
            width: '500px'
          },
          id: 'otsc-unregister-from-env'
        }
        ).afterClosed.subscribe((isPrimaryBtnClicked: boolean) => {
          if (isPrimaryBtnClicked) {
            this.agentService.unregisterFromEnv(this.selectedSystem.macAddress, event.data[0].id).pipe(
              tap(resp => {
                if(!resp.body.success){
                  let msg = new MsgPayLoad(undefined, {
                    message: $localize`Error unregistering ${this.selectedSystem.systemName}. Please try again.`
                  });
                  msg.setEvent(Events.ErrorMsg);
        
                  Utils.addHeaderMessageBanner(this.eventService, msg);
                }
              })).subscribe();
          }
          sub.unsubscribe();
        });
    } else if (event.name == "Unregister") {
        this.unregisterSystem(this.selectedSystem, this.selectedSystem.environments);
    }
  }

  clearOSInterval(): void {
    if (this.systemInfoStatusHandle) {
      clearInterval(this.systemInfoStatusHandle);
      this.systemInfoStatusHandle = 0;
    }
  }

  getLogLevelName(value: string): string {
    return this.logLevels.filter(level => level.value == value)[0].name;
  }

  getHttpLogLevelName(value: string): string {
    return this.httpLogLevels.filter(level => level.value == value)[0].name;
  }

  getPluginString(plugins: Plugin[]): string {
    return plugins.map(plugin => plugin.name + ": " + plugin.version).join('</br>');
  }

  getINetAddressString(addresses: string[]): string {
    return addresses.map(address => address).join('</br>');
  }

  registerCurrentSystem(): void {
    this.router.navigate(["/systems/register/", this.selectedSystem.agentID]);
  }

  unRegisterCurrentSystem(): void {
    this.unregisterSystem(this.selectedSystem, this.selectedSystem.environments);
  }

  enableCurrentSystem(): void {
    this.enableSystem(this.selectedSystem.macAddress, this.selectedSystem.systemName);
  }

  disableCurrentSystem(): void {
    this.disableSystem(this.selectedSystem.macAddress, this.selectedSystem.systemName);
  }

  removeCurrentSystem(): void {
    this.removeSystem(this.selectedSystem.macAddress, this.selectedSystem.systemName);
  }

  back():void {
    this.router.navigate(["/systems/overview"]);
  }

  getCurrentSystemInformation(): void {
    this.router.navigate(["/systems/information/", this.selectedSystem.agentID]);
  }

  openDiscoverProducts(): void {
    if (this.selectedSystem.isOnline) {
      const sub: Subscription =
        this.modal.open(ModalMessageComponent, {
          data: {
            title: $localize`Discover products`,
            message: $localize`You are about to start the discover products process. This action might take a couple of minutes. This process cannot be paused or aborted.`,
            primaryBtn: $localize`Start`,
            secondaryBtn: $localize`No`,
            width: '500px'
          },
          id: 'otsc-start-discover-dialog'
        }).afterClosed.subscribe((isPrimaryBtnClicked: boolean) => {
          if (isPrimaryBtnClicked) {
            this.softwareService.sendDiscoverProducts(this.wsService.getClientId(), this.wsService.getWSEnvId(), this.addTransaction(),
              new DiscoverProductsRequest(this.selectedSystem.macAddress, this.authService.getLoggedInUser().userName, true)).pipe(
            tap(resp => {
            }),
            catchError(resp => {
              Utils.addTaskErrorHeaderMessageEventBanner(this.eventService, "DISCOVER_PRODUCTS", this.selectedSystem.systemName);
              return of(resp);
            })).subscribe();
          }
          sub.unsubscribe();
        });
    }
  }

  editSystem(): void {
    this.isEditing = true;
    this.nickNameFormCtrl.setValue(this.selectedSystem.nickName);
    this.nickNameFormCtrl.addAsyncValidators([this.validateName(this.selectedSystem?.macAddress)]);
    this.descriptionFormCtrl.setValue(this.selectedSystem.description);
    this.logLevelFormCtrl.setValue(this.selectedSystem.logLevel);
    this.httpLogLevelFormCtrl.setValue(this.selectedSystem.httpLogLevel);
  }

  saveSystem(): void {
    this.isEditing = false;
    this.updateSystemInfo();
  }

  cancelEdit(): void {
    this.isEditing = false;
  };

  getSystemInfo(startTask: boolean): void {
    this.agentService.getSystemInfo(this.wsService.getClientId(), this.wsService.getWSEnvId(), this.addTransaction(),
      this.selectedSystem.macAddress).pipe(
        tap(resp => {
          if(startTask){
            this.systemInfoStatusHandle = setInterval(() => { 
              this.getSystemInfo(false);
            }, 1200);
          }
        })).subscribe();
  }

  processSystemInformation(resp: any): void {
    if (resp.body.ok) {
      let sysInfo = JSON.parse(resp.body.msg);
      if (sysInfo != undefined) {
        // set CPU data
        if (this.cpuChartData.datasets[0].data.length > 15) {
          this.cpuChartData.datasets[0].data.shift();
          this.cpuChartData.labels.shift();
        }
        this.cpuChartData.labels.push('');
        this.cpuChartData.datasets[0].data.push(sysInfo.cpuLoad);
        
        //set memory data
        this.memoryChartData.datasets[1].label = sysInfo.memoryTotal + 'GB';
        this.memoryChartData.labels =  [$localize`Used ${sysInfo.memoryUsed}GB`, $localize`Free ${sysInfo.memoryFree}GB`];
        this.memoryChartData.datasets[0].data = [sysInfo.memoryUsed, sysInfo.memoryFree];
        
        //set disk data
        this.diskChartData.datasets[1].label = sysInfo.diskTotal + 'GB';
        this.diskChartData.labels =  [$localize`Used ${sysInfo.diskUsed}GB`, $localize`Free ${sysInfo.diskFree}GB`];
        this.diskChartData.datasets[0].data = [sysInfo.diskUsed, sysInfo.diskFree];

        //update charts
        this.charts?.forEach(child => {
          child.update();
        });
      }
    }
  }

  openDisableProductDialog(): void {
    this.softwareService.getEnabledInstanceCount(this.selectedProduct.productId, this.selectedProduct.environment.id, this.selectedProduct.productVersion,
      this.selectedProduct.productGroupId, this.selectedSystem.macAddress, this.selectedProduct.installationDirectory
    ).pipe(
      tap(resp => {
        let lastInstance: boolean = (resp.body == 1);
        let isDisableMaster: boolean = (this.selectedProduct.isMasterSystem && !lastInstance);
        const sub: Subscription =
          this.modal.open(ModalMessageComponent, {
            data: {
              type: (isDisableMaster) ? ModalMessageType.Error : ModalMessageType.Warning,
              title: isDisableMaster ? $localize`Disable primary system '${this.selectedProduct.productName}'` :
                $localize`Disable product '${this.selectedProduct.productName}'`,
              message: (isDisableMaster) ?
                $localize`The primary system cannot be disabled while other instances are still active.  If you need to disable the primary system please select an alternate system as your primary system first.` :
                (lastInstance) ? "You are about to disable the last component instance for the specified product. By disabling this instance the product will no longer be managed, do you still wish to proceed?" :
                  "Are you sure you want to disable the product?",
              primaryBtn: (isDisableMaster) ? $localize`Close` : $localize`Disable`,
              secondaryBtn: $localize`No`,
              width: '600px'
            },
            id: isDisableMaster ? 'otsc-disable-master-product-dialog' : 'otsc-disable-product-dialog'
          }).afterClosed.subscribe((isPrimaryBtnClicked: boolean) => {
            if (isPrimaryBtnClicked && !isDisableMaster) {
                this.softwareService.sendDisableProduct(this.wsService.getClientId(), this.selectedProduct.environment.id, this.addTransaction(),
                new DisableProductRequest(this.selectedSystem.systemName, this.selectedProduct.installationDirectory, this.selectedProduct.productVersion,
                  this.selectedProduct.productName, this.selectedSystem.macAddress, this.selectedProduct.productId, this.authService.getLoggedInUser().userName)).pipe(
                tap(resp => {
                }),
                catchError(resp => {
                  Utils.addTaskErrorHeaderMessageEventBanner(this.eventService, "DISABLE_PRODUCT", this.selectedProduct);
                  return of(resp);
                })).subscribe();
            }
            sub.unsubscribe();
          });

      })).subscribe();
  }

  updateSystemInfo(): void {
    this.agentService.updateAgentInfo(this.selectedSystem.macAddress, this.nickNameFormCtrl.value,
      this.descriptionFormCtrl.value == undefined ? "" : this.descriptionFormCtrl.value,
      this.logLevelFormCtrl.value, this.httpLogLevelFormCtrl.value).pipe(
        tap(resp => {
          let msg = new MsgPayLoad(undefined, {});
          msg.setEvent(Events.AgentRefresh);
          this.eventService.broadcastEventMessage(msg);
          this.editSystemForm.markAsPristine();
        })).subscribe();
  }
  
  startManualDiscover(): void {
    this.router.navigate(["/systems/discover/", this.selectedSystem.agentID]);
  }

  getAgent(): void {
    this.agentService.getAgent(this.selectedSystemId).pipe(
      tap(resp => {
        this.products = [];
        this.environments = [];
        this.selectedSystem = resp.body;
        if (this.selectedSystem.description == undefined || this.selectedSystem.description == "undefined") {
          this.selectedSystem.description = "";
        }

        if (this.selectedSystem.products.length > 0) {
          this.sortSystems();
          this.selectedSystem.products.forEach(product => {
            product.supportedStatusExt = new SupportedStatusExt(product.supported,
              product.supported ? 'otsc-icon-status-available' : 'otsc-icon-status-busy',
              product.supported ? $localize`Supported` : $localize`Not supported`);
            product.enabledStatusExt = new SupportedStatusExt(product.isEnabled,
              product.isEnabled ? 'otsc-icon-status-available' : 'otsc-icon-status-busy',
              product.isEnabled ? $localize`Enabled` : $localize`Not enabled`);
  
            let tmpRow = new DataTableRow(product, null);
            if (product.supported) {
              tmpRow.actions = new Array<ActionMenuItem>(
                product.isEnabled ? new ActionMenuItem("Disable", $localize`Disable`, ActionMenuIcon.DELETE, product, false) :
                  new ActionMenuItem("Enable", $localize`Enable`, ActionMenuIcon.ADD, product, false)
              );
            }
  
            this.products.push(tmpRow);
          });
        }
  
        this.productsTab.count = this.products.length;
        this.environmentsTab.count = this.selectedSystem?.environments.length;
        this.agentStatus = Utils.getAgentStatus(this.selectedSystem);
        if(this.agentStatus.className.indexOf('spinner') > 0){
          this.agentStatus.className += " otsc-small-spinner";
        }
        this.productsTab.show = this.selectedSystem.isAuthorized;

        if (this.selectedSystem.environments.length > 0) {
          this.selectedSystem.environments = this.sortEnvironments(this.selectedSystem.environments);
          this.selectedSystem.environments.forEach(env => {
            this.environments.push(new DataTableRow(env, this.selectedSystem.environments.length > 1 ? [new ActionMenuItem("RemoveEnv", $localize`Remove`, ActionMenuIcon.DELETE, env, false)] : [new ActionMenuItem("Unregister", $localize`Remove`, ActionMenuIcon.DELETE, env, false)]));
          });
        }
        this.environmentsTab.show = this.selectedSystem.isAuthorized;
      }),
      catchError(resp => {
        let msg = new MsgPayLoad(undefined, {
          message: $localize`Error getting the system information. Please try again.`
        });
        msg.setEvent(Events.ErrorMsg);
        Utils.addHeaderMessageBanner(this.eventService, msg);
        this.back();
        return of(resp);
      })).subscribe();
  }

  sortSystems(): void {
    this.selectedSystem.products.sort((a, b) => {
      if(a.productName > b.productName) return 1;
      if(a.productName < b.productName) return -1;
      if(a.componentName > b.componentName) return 1;
      if(a.componentName < b.componentName) return -1;
      let aVers = a.productVersion.split('.');
      let bVers = b.productVersion.split('.');
      if(aVers.length < bVers.length){
        do{
          aVers.push("0");
        } while(aVers.length < bVers.length);
      }
      if(bVers.length < aVers.length){
        do{
          bVers.push("0");
        } while(bVers.length < aVers.length);
      }
      if(parseInt(aVers[0]) > parseInt(bVers[0])) return 1;
      if(parseInt(aVers[0]) < parseInt(bVers[0])) return -1;
      if(parseInt(aVers[1]) > parseInt(bVers[1])) return 1;
      if(parseInt(aVers[1]) < parseInt(bVers[1])) return -1;
      if(aVers.length > 2){
        if(parseInt(aVers[2]) > parseInt(bVers[2])) return 1;
        if(parseInt(aVers[2]) < parseInt(bVers[2])) return -1;
      }
      if(a.installationDirectory > b.installationDirectory) return 1;
      if(a.installationDirectory < b.installationDirectory) return -1;
    });
  }

  isSelectedSystem(): boolean {
    return this.selectedSystem !== undefined;
  }

  isSelectedSystemDisabled(): boolean {
    return (this.selectedSystem.isAuthorized && !this.selectedSystem.isActive);
  }

  toggleMoreActionsMenu(): void {
    this.showMoreActionsMenu = !this.showMoreActionsMenu;
  }

  @HostListener("document:click", ["$event"])
  clickout($event) {
    if(this.moreActionsMenu && !this.moreActionsMenu.nativeElement.contains($event.target)){
      this.showMoreActionsMenu = false
    }
  }

  canDeactivate(): boolean {
    return !this.isEditing && !this.editSystemForm.dirty;
  }

  getDeactivateModalMessageConfig(): ModalConfig {
    return Utils.getDefaultDeactivateModalMessageConfig('otsc-system-edit-unsaved-changes');
  }
}